import React, { useState, useEffect, useRef, useCallback } from "react"
import SearchDropDown from "../comp/SearchDropDown"
import Tip from "../comp/Tip"
import People from "../comp/People"
import Button from "../comp/Button"
import CheckBoxes, { CheckBox } from "../comp/CheckBoxes"
import isFQDN from "validator/lib/isFQDN"
import { Template } from "./PersonAdder"
import BarLoader from "react-spinners/BarLoader"
import { openProTrial } from "./billing/billingIssues"
import { PRO_TRIAL_DAYS } from "../services/billing/billing.const"
import { isGtGr } from "../utils/utils"
import { isOrgManagerForMission } from "../utils/org-permissions"
import { screenOrgPerson } from "../utils/allocation"
import cx from "classnames"
import Textarea from "react-textarea-autosize"
import { toast } from "react-toastify"
import isEmail from "validator/lib/isEmail"
import { getObjectId, getRefId, sortAlpha, getDomain } from "../comp/Utils"

import { PiWarningDuotone, PiQuestionDuotone } from "react-icons/pi"
import { formatMissionPeople } from "../comp/MissionUtils"
import { addParam } from "../comp/History"
import { PiTriangleDuotone, PiEnvelopeDuotone } from "react-icons/pi"
import apiAuth from "../services/auth/auth.api"
import { getVerifyReturnUrl, verifyOpenMissionInvite } from "../utils/emailVerify"

import {
    getMissionUsageByType,
    hasMissionUnlimitedPeopleUsage,
    getMissionSubscriptionPlanItemByType,
} from "./StripeUtils"

import "../comp/InviteToMission.scss"
import "../comp/PersonAdder.scss"

/**
 * @param [verifyOpen] {string} The card its verification email reopens (utils/emailVerify.js);
 *   defaults to the mission's invite card
 */
const InviteToMission = ({ missionData, app, noCreateRoleOption, onCancel, afterInvite, verifyOpen }) => {
    // Refs
    const emailDropDownRef = useRef(null)
    const titleFieldRef = useRef(null)
    const changeCheckerTimeoutRef = useRef(null)
    const emailTimeoutRef = useRef(null)

    // Derived data from props
    const org = missionData.org ? app.state.orgs.find((o) => getObjectId(o) === getObjectId(missionData.org)) : null
    const meInMission = missionData.people.find((p) => getRefId(p) === app.state.person._id)
    const meInOrg = org ? (org.people || []).find((p) => getRefId(p) === app.state.person._id) : null

    const aboutMe = missionData.people.find((p) => getRefId(p) === app.state.person._id)
    const myEmail =
        aboutMe.email === app.state.person.email
            ? { email: app.state.person.email, verified: app.state.person.emailVerified }
            : app.state.person.emailList.find((em) => em.email === aboutMe.email) || { verified: false }

    const autoCreateRoleInitial =
        !noCreateRoleOption &&
        !missionData.isTemplate &&
        !missionData.isModel &&
        !(
            missionData.client &&
            meInOrg?.permission !== 2 &&
            !meInOrg?.canApproveRoles &&
            missionData.departmentTags?.includes("673c9e5d9794765732fd2125") &&
            missionData.offices?.includes("66e3db6b0adca66cf7368435")
        )

    const [searchList, setSearchList] = useState([])

    useEffect(() => {
        if (!org) return []

        let goodPeople = []
        let issuePeople = []
        let externalPeople = []

        org.people?.forEach((orgPerson) => {
            if (orgPerson?.email === "help@missionx.ai") return
            if (missionData.people?.find((p) => p.permission <= 3 && getRefId(p) === getRefId(orgPerson))) return
            if (orgPerson?.permission === 9) {
                externalPeople.push({ ...orgPerson, external: true })
            } else if (
                screenOrgPerson({
                    mission: missionData,
                    orgData: org,
                    person: orgPerson,
                    departments: missionData.departmentTags,
                    offices: missionData.offices,
                    app: app,
                })
            ) {
                goodPeople.push(orgPerson)
            } else {
                issuePeople.push({ ...orgPerson, issue: true })
            }
        })

        setSearchList([...sortAlpha(goodPeople), ...sortAlpha(externalPeople), ...sortAlpha(issuePeople)])
    }, [missionData.people])

    // Prepare search list

    // State
    const [state, setState] = useState({
        myEmail,
        checkedBoxes: { admin: false, manager: false, team: true, observer: false },
        loading: false,
        autoCreateRole: autoCreateRoleInitial,
        verified: myEmail.verified,
        msg: missionData.isTemplate ? "Please help manage our template" : "Welcome to the mission!",
        email: null,
        emailSearch: null,
        error: false,
        showDomainChangeMsg: false,
        showButton: false,
        invitedName: null,
        personData: null,
        isPm: false,
    })

    const [permission, setPermission] = useState(null)

    // Effects

    // Verified in another tab while the card is open
    useEffect(() => {
        if (myEmail.verified && !state.verified) setState((s) => ({ ...s, myEmail, verified: true }))
    }, [myEmail.verified])

    useEffect(() => {
        if (emailDropDownRef.current && emailDropDownRef.current.focus) {
            setTimeout(() => {
                if (emailDropDownRef.current && emailDropDownRef.current.focus) {
                    emailDropDownRef.current.focus()
                }
            }, 1000)
        }
    }, [])

    useEffect(() => {
        return () => {
            clearTimeout(changeCheckerTimeoutRef.current)
            clearTimeout(emailTimeoutRef.current)
        }
    }, [])

    // Event handlers
    const handlePermissions = useCallback(
        (arr, ind, isChecked, obj) => {
            let checks = { admin: false, manager: false, team: false, observer: false }
            checks[obj.id] = true

            let newPermission = permission
            let newIsPm = state.isPm

            if (obj.id === "observer" && isChecked) {
                newIsPm = false
                newPermission = obj?.value
            } else if (obj.id === "team" && isChecked) {
                newIsPm = false
                newPermission = obj?.value
            } else if (obj.id === "admin" || obj.id === "pm") {
                newIsPm = true
                newPermission = obj?.value
            } else if (obj.id === "manager") {
                newIsPm = false
                newPermission = obj.value
            }

            setPermission(newPermission)
            setState((prev) => ({
                ...prev,
                checkedBoxes: checks,
                isPm: newIsPm,
                autoCreateRole: obj.id === "observer" ? false : prev.autoCreateRole,
            }))
        },
        [permission, state.isPm],
    )

    const setErrorMessage = useCallback(() => {
        if (!state.error) {
            const any = missionData?.inviteDomains?.includes("ANY")
            const validDomain = any || missionData?.inviteDomains?.includes(getDomain(state.email))

            setState((prev) => ({
                ...prev,
                error: prev.email?.length && !isEmail(prev.email),
                showDomainChangeMsg: isEmail(prev.email || "x") && !validDomain,
            }))
        }
    }, [missionData, state.email, state.error])

    const doInvite = useCallback(async () => {
        if (!isEmail(state.email)) {
            toast.error("The email you entered appears incorrect.")
            return
        }

        const alreadyInMission = missionData.people.find((p) => p.permission <= 3 && p.email === state.email)
        if (alreadyInMission) {
            toast.warning(`Ooops. ${state.email} already exists on the mission team.`, { autoClose: 6000 })
            resetAll()
            // Still theirs to use: inviting from a task puts the person on the task.
            if (afterInvite) afterInvite(alreadyInMission)
            return
        }

        const isThereAnOrgPerson = org?.people?.find((p) => p.email === state.email)

        if (isThereAnOrgPerson && meInOrg?.accessibleDepartments?.length && isThereAnOrgPerson?.departments?.length) {
            if (
                meInOrg?.accessibleDepartments?.length &&
                !meInOrg?.accessibleDepartments?.includes(isThereAnOrgPerson?.departments[0])
            ) {
                toast.error(
                    "Sorry but this person falls outside of your accessible department's and cannot be added to the project",
                    { autoClose: 6000 },
                )
                return
            }
        }

        const metadata = {
            config: {
                role: "Team member",
                permission: permission || 0,
                isProjectManager: state.isPm,
            },
            message: state.msg,
            invitee: state.personData,
            autoCreateRole: state.autoCreateRole,
        }

        setState((prev) => ({
            ...prev,
            loading: true,
            showButton: false,
        }))

        try {
            const res = await app.missionInvitePerson(missionData._id, state.email, metadata)
            resetAll()
            if (afterInvite) {
                try {
                    afterInvite(res)
                } catch (e) {
                    app.silentFail("wqjklrhkwejjrlkweffekd")
                }
            }
        } catch (error) {
            toast.error("Failed to send invite. Please try again.")
            setState((prev) => ({ ...prev, loading: false }))
        }
    }, [
        state.email,
        state.msg,
        state.personData,
        state.isPm,
        state.autoCreateRole,
        missionData,
        org,
        meInOrg,
        permission,
        app,
        afterInvite,
    ])

    const resetAll = useCallback(() => {
        setState((prev) => ({
            ...prev,
            email: null,
            emailSearch: null,
            msg: "Welcome to the project!",
            checkedBoxes: { admin: false, manager: false, team: true, observer: false },
            invitedName: null,
            loading: false,
            isPm: false,
            showDomainChangeMsg: false,
            personData: null,
            error: false,
            showButton: false,
        }))

        setPermission(null)

        setTimeout(() => {
            try {
                if (emailDropDownRef.current) {
                    emailDropDownRef.current.focus()
                }
                if (titleFieldRef.current) {
                    titleFieldRef.current.value = ""
                }
            } catch (err) {
                // Silent fail
            }
        }, 800)
    }, [])

    const onEmailChange = useCallback(
        (val, paste, e) => {
            clearTimeout(emailTimeoutRef.current)
            const isOk = isEmail(val)
            const domains = missionData.inviteDomains || []

            let searchString = val ? val.trim() : ""
            const domIndex = searchString.indexOf("@")
            const hasLastChar = searchString.charAt(domIndex + 1) === "" ? false : true
            const any = domains.includes("ANY")

            let theEmail

            if (!paste && !any) {
                if (domIndex !== -1 && domains.length === 1 && !hasLastChar) {
                    searchString = searchString.concat(domains[0])
                    if (e && e.target) e.target.blur()
                } else if (domIndex !== -1 && hasLastChar && domains.length > 1) {
                    const lastPortion = searchString.substr(searchString.indexOf("@") + 1, searchString.length)
                    const domainMatches = domains.filter((dm) => dm.startsWith(lastPortion))

                    if (domainMatches.length === 1) {
                        let remainder = domainMatches[0].replace(lastPortion, "")
                        searchString = searchString.concat(remainder)
                        if (e && e.target) e.target.blur()
                    }
                }

                const isEmailValid = isEmail(searchString?.trim())
                theEmail = isEmailValid ? searchString : null
            } else {
                theEmail = isEmail(val?.trim()) ? val : null
            }

            setState((prev) => ({
                ...prev,
                emailSearch: theEmail ? null : searchString,
                email: theEmail,
                showButton: !!theEmail && isEmail(theEmail?.trim()),
                invitedName: null,
                personData: theEmail ? prev.personData : null,
                error: false,
            }))

            clearTimeout(changeCheckerTimeoutRef.current)
            changeCheckerTimeoutRef.current = setTimeout(() => {
                const showDomMsg =
                    isOk && domains.length > 0 && !domains.includes(val.substr(val.indexOf("@") + 1, val.length))
                setState((prev) => ({
                    ...prev,
                    showDomainChangeMsg: showDomMsg && domains.indexOf("ANY") === -1,
                }))
            }, 700)
        },
        [missionData.inviteDomains],
    )

    const onSelectHandler = useCallback((obj) => {
        if (!obj) return

        if (obj.issue) {
            toast.warning(
                "Sorry but you do not have sufficient permission to add this person. Please ask an organization admin if possible.",
                { autoClose: 9000 },
            )
            return
        }

        setState((prev) => ({
            ...prev,
            email: obj.email,
            invitedName: obj.ref?.firstName || "",
            personData: obj,
            showButton: true,
            emailSearch: null,
            error: false,
        }))
    }, [])

    const matchPerson = useCallback(
        (obj, val) => {
            if (!val) return true
            const person = obj.data

            if (!person.ref?.firstName && !person.ref?.lastName) {
                return false
            }

            let value = val
            if (value === "" && !state.email) {
                return true
            } else if (value === "" && state.email) {
                return false
            } else if (
                (person.ref.firstName && person.ref.firstName.toLowerCase().includes(value.toLowerCase())) ||
                (person.ref.lastName && person.ref.lastName.toLowerCase().includes(value.toLowerCase())) ||
                (person.email && person.email.toLowerCase().includes(value.toLowerCase()))
            ) {
                return true
            } else {
                return false
            }
        },
        [state.email],
    )

    const extraPermissions = useCallback((arr, index, checked, obj) => {
        if (obj.value === "autoCreateRole") {
            setState((prev) => ({ ...prev, autoCreateRole: checked }))
        }
    }, [])

    const getExtraOptions = useCallback(
        (notFoundInOrg) => {
            let options = []
            let createRoleOption = !missionData.noTimesheets

            if (noCreateRoleOption) {
                createRoleOption = false
            } else if (!org?.isLicensedActive) {
                createRoleOption = false
            } else if (state.checkedBoxes["observer"]) {
                createRoleOption = false
            } else if (
                missionData.client &&
                meInOrg?.permission !== 2 &&
                !meInOrg?.canApproveRoles &&
                missionData.departmentTags?.includes("673c9e5d9794765732fd2125") &&
                missionData.offices?.includes("66e3db6b0adca66cf7368435")
            ) {
                createRoleOption = false
            }

            if (
                !missionData.createdFromGtCrm &&
                !state.checkedBoxes["observer"] &&
                !missionData.isModel &&
                !missionData.isTemplate
            ) {
                if (createRoleOption) {
                    options.push({
                        label: (
                            <>
                                Enable timesheets (Assign a project role)
                                <Tip title='Will automatically create a role and attach this person to it in the "Budget" panel. They will then be active and able to submit timesheets. '>
                                    <PiQuestionDuotone />
                                </Tip>
                            </>
                        ),
                        value: "autoCreateRole",
                        checked: state.autoCreateRole,
                    })
                }
            }

            return options
        },
        [missionData, org, meInOrg, noCreateRoleOption, state.checkedBoxes, state.autoCreateRole],
    )

    // Render logic
    const orgDepartment = org ? org.departments?.find((d) => missionData.departmentTags?.includes(d._id)) : null

    const isOrgAdmin = isOrgManagerForMission(meInOrg, missionData)
    const isProjectManager = meInMission?.permission === 2 || meInMission?.isProjectManager || isOrgAdmin

    let notFoundInOrg = false
    if (org && state.email && isEmail(state.email)) {
        const dom = getDomain(state.email)
        notFoundInOrg = !org?.inviteDomains?.find((id) => id === dom)

        if (state.checkedBoxes["admin"] && notFoundInOrg) {
            handlePermissions([], 0, true, { id: "team", value: 0 })
            toast("Permission admin is not permitted for domains that are not approved by the organization.", {
                autoClose: 4000,
            })
        }
    }

    const gtNo = missionData.createdFromGtCrm || orgDepartment?.rolesRequireApproval

    let options = []

    if (meInMission.permission === 2 || meInOrg?.permission === 2) {
        if (!gtNo)
            options.push({
                value: 2,
                id: "admin",
                disabled: notFoundInOrg,
                label: (
                    <p>
                        Admin - {missionData?.isTemplate ? "Template" : "Project"} owner{" "}
                        {state.checkedBoxes["admin"] && <>💥</>}
                        <Tip
                            title={
                                missionData.isTemplate
                                    ? "Admins can do anything including delete the template"
                                    : notFoundInOrg
                                      ? `The email domain has not been cleared by the Organization. The person can still work on the project, but their permission is limited as Admins
                                            see potentially sensitive client data. To change this, an org admin must add "
                                            ${getDomain(state.email)}" to the domains in the org config panel.`
                                      : "Admins can do absolutely anything a Project manager can do AND delete the project."
                            }
                        >
                            <PiQuestionDuotone color={notFoundInOrg ? "red" : undefined} />
                        </Tip>
                    </p>
                ),
                checked: state.checkedBoxes["admin"],
            })
    }

    if (isProjectManager && !gtNo) {
        options.push({
            value: 1,
            id: "pm",
            label: (
                <p>
                    {notFoundInOrg ? (
                        <>
                            {missionData.isTemplate ? "Template manager (Limited)" : "Project Manager (Limited)"}{" "}
                            {state.checkedBoxes["pm"] && <>💪</>}
                        </>
                    ) : (
                        <>
                            {missionData.isTemplate ? "Template manager" : "Project Manager"}{" "}
                            {state.checkedBoxes["pm"] && <>💪</>}
                        </>
                    )}
                    <Tip
                        title={
                            missionData.isTemplate
                                ? "Template managers can do almost anything except delete the template"
                                : notFoundInOrg
                                  ? "Although this person can act as a project manager, they will not have access to change settings, as these may contain potentially sensitive data such as client information and department details."
                                  : "PMs along with Admin's control the project plan, the budget, and approve leaves and timesheets for the team."
                        }
                    >
                        <PiQuestionDuotone color={notFoundInOrg ? "red" : undefined} />
                    </Tip>
                </p>
            ),
            checked: state.checkedBoxes["pm"],
        })
    }

    if (meInMission.permission >= 0 && !missionData.isTemplate) {
        if (!(missionData.isProcess && missionData.isTemplate)) {
            if (meInMission.permission >= 1 && missionData.projectType !== "mx-gantt" && !missionData.isTemplate) {
                options.push({
                    value: 1,
                    id: "manager",
                    label: (
                        <p>
                            Team lead{" "}
                            <Tip title="Can create and assign tasks to project members">
                                <PiQuestionDuotone />
                            </Tip>
                        </p>
                    ),
                    checked: state.checkedBoxes["manager"],
                })
            }
            options.push({
                value: 0,
                id: "team",
                label: (
                    <p>
                        Team member{" "}
                        <Tip title="Team members can only work on tasks they are assigned to and cannot create new tasks">
                            <PiQuestionDuotone />
                        </Tip>
                    </p>
                ),
                checked: state.checkedBoxes["team"],
            })

            if (!missionData.isModel && org) {
                options.push({
                    value: 3,
                    id: "observer",
                    label: (
                        <>
                            Observer (Internal/External)
                            <Tip title="People observing cannot edit the project or budget. Observers from within the organization will have more access to view data depending on their organization permissions. External observers like clients, can see progress, tasks, approve change requests and reports and message people.">
                                <PiQuestionDuotone />
                            </Tip>
                        </>
                    ),
                    checked: state.checkedBoxes["observer"],
                })
            }
        }
    }

    const usage = getMissionUsageByType("people", missionData)
    const peepsToShow = formatMissionPeople(missionData.people.filter((p) => p.permission !== 5 && p.permission !== 3))

    let howManyAreObservers = 0
    if (org) {
        howManyAreObservers = missionData.people.filter((p) => p.permission > 3).length
    }

    if (
        !hasMissionUnlimitedPeopleUsage(missionData) &&
        !getMissionSubscriptionPlanItemByType("people", missionData) &&
        usage.value >= 5
    ) {
        return (
            <div className="dna-invite-card dna-invite-to-mission" id="invite-to-mission-card">
                <div className="dna-col">
                    <h2>Time to level up 🦖</h2>
                    <br />
                    <People data={peepsToShow} />
                    <br />
                    <h3>5 of 5 free slots used.</h3>

                    {howManyAreObservers > 0 && (
                        <p className="x-inset">
                            There are {howManyAreObservers} {howManyAreObservers === 1 ? "person" : "people"} from your
                            Organization that {howManyAreObservers === 1 ? "is" : "are"} using a free seat on this
                            project. Org managers count as a seat because although not directly involved they get all
                            the insights and reports and can follow the project.{" "}
                        </p>
                    )}

                    {org && !org.isLicensedActive ? (
                        <>
                            <p>Why not start a free {PRO_TRIAL_DAYS}-day Pro trial for your organization?</p>
                            <Button
                                red
                                onClick={() => {
                                    if (meInOrg?.permission === 1 || meInOrg?.permission === 2) {
                                        openProTrial(org, app)
                                    } else {
                                        toast("Ooops. Please ask an organization manager to activate the license.")
                                    }
                                }}
                            >
                                Start a free Pro trial
                            </Button>
                            <br />
                            <br />
                        </>
                    ) : (
                        <div
                            className="dna-pill-box x-pill click-able"
                            onClick={(e) => {
                                addParam("mc", "open")
                            }}
                        >
                            Upgrade just your project
                        </div>
                    )}
                </div>
            </div>
        )
    }

    const extraOptions = getExtraOptions(notFoundInOrg)

    return (
        <div className="dna-invite-to-mission" id="invite-to-mission-card">
            <div className="x-action-bg" style={{ backgroundImage: missionData.theme?.backgroundImage }}></div>

            {state.verified && (
                <div
                    className={cx("dna-avatar-round x-inv-avatar", { filled: state.personData })}
                    style={{
                        backgroundImage: state.personData?.ref
                            ? `url(${state.personData.ref.avatar})` || "url(/img/avatar-blur-red.jpg)"
                            : "url(/img/avatar-my-1.jpg)",
                    }}
                ></div>
            )}

            {state.loading && (
                <div className="dna-col">
                    <p style={{ marginTop: 90 }}>Sending invite...</p>
                    <BarLoader color={"aqua"} loading={state.loading} />
                </div>
            )}

            {!state.loading && (
                <>
                    {!state.verified && (
                        <>
                            <div className="x-inset w-500" style={{ marginTop: 40 }}>
                                <div className="dna-flex">
                                    <img src="/img/mAX.png" style={{ width: 80, marginRight: 10 }} />
                                    <div>
                                        <p>
                                            <b>Houston... we have an unverified email.</b>
                                        </p>
                                        <p>
                                            Mission safety comes first. Please take a moment to validate your email
                                            address so we can invite your team.
                                        </p>

                                        <p
                                            className="x-link"
                                            onClick={() => {
                                                apiAuth
                                                    .sendEmailVerification(
                                                        state.myEmail.email,
                                                        getVerifyReturnUrl(
                                                            verifyOpen || verifyOpenMissionInvite(getObjectId(missionData))
                                                        )
                                                    )
                                                    .then(() => {
                                                        toast.success("Verification email on it's way!")
                                                        if (onCancel) onCancel()
                                                    })
                                                    .catch((err) => {
                                                        toast.error("Already on its way. Please check your email.")
                                                    })
                                            }}
                                        >
                                            Resend authentication email
                                        </p>
                                        <div style={{ textAlign: "right" }}>
                                            <b>mAX</b>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                    {state.verified && (
                        <>
                            <h3>Mission invite</h3>
                            {missionData.isModel ? (
                                <h4>
                                    Invite someone to help estimate this
                                    <br /> model project for the proposal.{" "}
                                </h4>
                            ) : missionData.isTemplate ? (
                                <h4>Invite someone to help craft the template.</h4>
                            ) : missionData.isProposal ? (
                                <h4>Invite someone to help prepare the proposal.</h4>
                            ) : (
                                <h4>Invite a new team member</h4>
                            )}
                            <SearchDropDown
                                ref={emailDropDownRef}
                                wrapperClassName={cx("w-500 dna-center-text x-inv-search", {
                                    "squigly-red": state.error,
                                })}
                                noAlpha={true}
                                maxItems={100}
                                value={state.email}
                                shouldItemRender={matchPerson}
                                data={searchList}
                                replaceTemplate={true}
                                selectOnBlur={false}
                                noEmptyMenu={true}
                                noResultsText="Inviting a person outside the existing team..."
                                onSelect={(label, obj) => {
                                    onSelectHandler(obj)
                                }}
                                showClearIcon={true}
                                onClear={() => {
                                    setState((prev) => ({
                                        ...prev,
                                        email: null,
                                        personData: null,
                                        error: false,
                                        showDomainChangeMsg: false,
                                        showButton: false,
                                    }))
                                }}
                                onBlur={() => {
                                    setErrorMessage()
                                }}
                                onChange={(text) => {
                                    setState((prev) => ({
                                        ...prev,
                                        showDomainChangeMsg: false,
                                        email: text,
                                        error: text.includes("@") && !isEmail(text.trim()),
                                        showButton: isEmail(text.trim()),
                                    }))
                                }}
                                onKeyDown={(e) => {
                                    e.persist()
                                    if (
                                        state.email?.length >= 1 &&
                                        e.key === "@" &&
                                        missionData.inviteDomains?.length === 1 &&
                                        missionData.inviteDomains[0] !== "ANY"
                                    ) {
                                        e.target.blur()
                                        e.stopPropagation()
                                        e.preventDefault()
                                        setState((prev) => ({
                                            ...prev,
                                            error: false,
                                            email: prev.email + "@" + missionData.inviteDomains[0],
                                            showButton: true,
                                        }))
                                    }

                                    if (e.key === "Enter") {
                                        setTimeout(() => {
                                            e.target.blur()
                                        }, 100)
                                    }
                                }}
                                placeHolder="Select a team member or enter a new email..."
                                template={(personDetails, isHighlighted) => {
                                    return (
                                        <span>
                                            <Template
                                                personDetails={personDetails}
                                                isHighlighted={isHighlighted}
                                                orgData={org}
                                                app={app}
                                                mission={missionData}
                                                selectedText={state.email}
                                                onSelectHandler={() => {
                                                    onSelectHandler(personDetails)
                                                }}
                                                comp={
                                                    <>
                                                        {personDetails.external && (
                                                            <p>
                                                                <span
                                                                    style={{
                                                                        marginTop: 4,
                                                                        fontWeight: "bold",
                                                                        fontSize: 11,
                                                                    }}
                                                                    className="dna-issue-text"
                                                                >
                                                                    EXTERNAL RESOURCE
                                                                </span>{" "}
                                                                Please make sure you have permission to add this person
                                                            </p>
                                                        )}
                                                        {personDetails.issue && (
                                                            <p
                                                                style={{ marginTop: 4 }}
                                                                className="x-inset-light dna-smaller-text orange"
                                                            >
                                                                <PiTriangleDuotone color={"red"} /> This person cannot
                                                                be added because you do not have clearance either
                                                                because offices or departments do not align or shared
                                                                resources are not allowed for their department.
                                                            </p>
                                                        )}
                                                    </>
                                                }
                                            />
                                        </span>
                                    )
                                }}
                            />
                            <div
                                className={cx("x-inset-light w-500 orange x-inv-show-add-dom", {
                                    open: state.showDomainChangeMsg,
                                })}
                                style={{ marginBottom: 10 }}
                            >
                                <p>
                                    <b>
                                        <PiWarningDuotone color="orange" /> Unapproved email domain detected
                                    </b>
                                </p>
                                <p>This email has not been authorized in project settings.</p>
                                {isProjectManager && (
                                    <CheckBox
                                        controlled
                                        label={
                                            <>
                                                Authorize people from &nbsp;
                                                <span className="dna-hl red">{state.email?.trim()?.split("@")[1]}</span>
                                                ?
                                            </>
                                        }
                                        onChange={(a, b, checked) => {
                                            if (checked) {
                                                const newDomain = state.email?.trim()?.split("@")[1]

                                                if (isFQDN(newDomain)) {
                                                    let newDomains = [...(missionData.inviteDomains || [])]
                                                    newDomains.push(newDomain)
                                                    app.missionUpdate(missionData._id, {
                                                        inviteDomains: newDomains,
                                                    })

                                                    toast.success(
                                                        newDomain +
                                                            " has been cleared for the project. You can change domains in your project settings",
                                                        { autoClose: 6000 },
                                                    )

                                                    setState((prev) => ({
                                                        ...prev,
                                                        showDomainChangeMsg: false,
                                                    }))
                                                } else {
                                                    toast(
                                                        "Sorry but that domain may be invalid. Please try again or contact us for help. help@missionx.ai",
                                                        { autoClose: 5000 },
                                                    )
                                                }
                                            }
                                        }}
                                    />
                                )}
                            </div>
                            <h4>Include a personalized message</h4>
                            <Textarea
                                ref={titleFieldRef}
                                id="inv-msg"
                                className="w-500 inv-text"
                                onChange={(e) => {
                                    if (e.target?.value) {
                                        setState((prev) => ({ ...prev, msg: e.target?.value }))
                                    }
                                }}
                                value={state.msg}
                            />
                            <h4>Permission</h4>
                            <CheckBoxes controlled onChange={handlePermissions} data={options} />
                            {extraOptions.length > 0 && !orgDepartment?.rolesRequireApproval && (
                                <>
                                    <h4>Optional</h4>
                                    <CheckBoxes
                                        controlled
                                        key={extraOptions[0].disabled + "-cb-" + extraOptions.length}
                                        onChange={extraPermissions}
                                        data={extraOptions}
                                    />
                                </>
                            )}
                            {missionData.client &&
                                meInOrg?.permission !== 2 &&
                                !meInOrg?.canApproveRoles &&
                                missionData.departmentTags?.includes("673c9e5d9794765732fd2125") &&
                                missionData.offices?.includes("66e3db6b0adca66cf7368435") && (
                                    <div className="x-inset-light">
                                        <p>
                                            Please note: You may invite people to observe the project but only OERA
                                            approved people can be assigned a role from the Scheduling tab in the pm's
                                            WORK SPACE
                                        </p>
                                    </div>
                                )}
                            <br />
                            <div className="dna-center " style={{ paddingBottom: 10 }}>
                                {isEmail(state.email || "") && (
                                    <Button
                                        className="x-mission-invite-button"
                                        style={{
                                            marginLeft: 70,
                                            marginRight: 0,
                                            paddingRight: 40,
                                        }}
                                        animated
                                        flip
                                        icon
                                        dark
                                        spaceRight
                                        disabled={state.showDomainChangeMsg}
                                        onClick={doInvite}
                                    >
                                        <PiEnvelopeDuotone color="aqua" /> Send Invite
                                    </Button>
                                )}
                                &nbsp;
                                <Button clear onClick={onCancel}>
                                    Cancel
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    )
}

InviteToMission.displayName = "InviteToMission"
export default InviteToMission
